<template>
  <Breadcrumbs
      title="Create Module"
      :breadcrumbs="[
      { label: 'Master Data', path: '' },
      { label: 'Module & Route Configurations', path: '/module-route-configurations' },
      { label: 'Create Module', path: '/create-module-route-configuration' },
    ]"
  />

  <div class="container-fluid">
    <div class="row project-cards">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-body">
            <!-- Loading Indicator -->
            <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
              <div class="spinner-border" style="color: #003366; width: 3rem; height: 3rem;" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted" style="font-size: 1rem;">Fetching page types...</p>
            </div>

            <!-- Form Content -->
            <div v-else>
              <div class="row mb-4">
                <div class="col-12 form-group">
                  <label>Module Name <span class="text-danger">*</span></label>
                  <input type="text" v-model="moduleName" class="form-control" placeholder="Enter module name" style="text-transform: uppercase;" :disabled="isSaving" />
                </div>
              </div>

              <!-- Submodules Section -->
              <div class="row">
                <div class="col-12">
                  <h6 class="mb-0 font-weight-bold mb-3">Submodules</h6>
                  <div class="d-flex mb-3">
                    <input type="text" v-model="newSubmoduleName" class="form-control me-2" placeholder="Enter submodule name" @keyup.enter="!isSaving && addSubmodule()" :disabled="isSaving" />
                    <button class="btn text-nowrap" style="background-color: #0B0909; border-color: #0B0909; border-radius: 50px; color: #e0e0e0;" type="button" @click="addSubmodule" :disabled="isSaving">
                      {{ isEditingSubmodule ? 'Edit Submodule' : 'Add Submodule' }}
                    </button>
                    <button v-if="isEditingSubmodule" class="btn btn-outline-secondary ms-2 text-nowrap" style="border-radius: 50px;" type="button" @click="cancelEditSubmodule" :disabled="isSaving">
                      Cancel
                    </button>
                  </div>
                  
                  <!-- Submodules Cards -->
                  <div v-if="submodules.length > 0" class="position-relative mb-4 pb-2">
                    <button v-if="showLeftArrow" @click="scrollLeft" class="btn btn-light shadow position-absolute" style="top: 50%; left: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                      <i class="fa fa-chevron-left text-primary" style="margin-left: -2px;"></i>
                    </button>

                    <div ref="scrollContainer" @scroll="handleScroll" class="d-flex flex-nowrap overflow-auto hide-scrollbar py-2 px-1" style="gap: 15px;">
                      <div 
                        v-for="(subName, index) in submodules" 
                        :key="index" 
                        class="card mb-0 shadow-sm" 
                        style="min-width: 220px; flex: 0 0 auto; border: 1px solid #e0e0e0; border-radius: 8px;"
                      >
                        <div class="card-body d-flex flex-column justify-content-between p-3">
                          <div class="d-flex justify-content-between align-items-start mb-3">
                            <h6 class="font-weight-bold text-secondary text-truncate" :title="subName">
                              <i class="fa fa-folder-open me-2"></i> {{ subName }}
                            </h6>
                            <div>
                              <i class="fa fa-pencil text-primary me-2" @click="!isSaving && editSubmodule(subName)" title="Edit Submodule" :style="{ cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.5 : 1 }"></i>
                              <i class="fa fa-trash text-danger" @click="!(isSaving || (isEditingSubmodule && editingSubmoduleId === subName)) && removeSubmodule(subName)" title="Remove Submodule" :style="{ cursor: (isSaving || (isEditingSubmodule && editingSubmoduleId === subName)) ? 'not-allowed' : 'pointer', opacity: (isSaving || (isEditingSubmodule && editingSubmoduleId === subName)) ? 0.5 : 1 }"></i>
                            </div>
                          </div>
                          <div class="d-flex justify-content-between align-items-center mt-auto">
                            <span class="text-muted small">
                              <strong>{{ getRouteCount(subName) }}</strong> Route(s)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button v-if="showRightArrow" @click="scrollRight" class="btn btn-light shadow position-absolute" style="top: 50%; right: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                      <i class="fa fa-chevron-right text-primary" style="margin-right: -2px;"></i>
                    </button>
                  </div>
                </div>
              </div>

            <div class="d-flex justify-content-between align-items-center mb-3 mt-4">
              <h6 class="mb-0 font-weight-bold">Routes</h6>
              <button class="btn btn-sm text-white" style="background-color: #003366; border-color: #003366;" @click="addRouteRow" :disabled="isSaving">
                <i class="fa fa-plus me-1"></i> Add Route
              </button>
            </div>

            <div class="table-responsive">
              <table class="table table-bordered table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Route Name</th>
                    <th>Submodule</th>
                    <th>Page Type</th>
                    <th>URI</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(route, index) in routes" :key="index">
                    <td>
                      <input type="text" class="form-control" v-model="route.route_name" placeholder="Route Name" :disabled="isSaving" />
                    </td>
                    <td>
                      <select class="form-select" v-model="route.submodule_name" :disabled="isSaving">
                        <option value="">No Submodule</option>
                        <option v-for="subName in submodules" :key="subName" :value="subName">{{ subName }}</option>
                      </select>
                    </td>
                    <td>
                      <select class="form-select" v-model="route.page_type_id" :disabled="isSaving">
                        <option value="" disabled>Select Type</option>
                        <option v-for="type in availablePageTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" class="form-control" v-model="route.uri" placeholder="URI" style="text-transform: lowercase;" :disabled="isSaving" />
                    </td>
                    <td class="text-center align-middle">
                      <i class="fa fa-trash text-danger" @click="!isSaving && removeRouteRow(index)" title="Remove Route" :style="{ fontSize: '18px', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.5 : 1 }"></i>
                    </td>
                  </tr>
                  <tr v-if="routes.length === 0">
                    <td colspan="5" class="text-center text-muted py-4">No routes added. Click 'Add Route' to begin.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="text-end mt-4">
              <button class="btn btn-danger me-2" @click="cancel" :disabled="isSaving">Cancel</button>
              <button class="btn btn-success" @click="save" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isSaving ? 'Saving...' : 'Save Module' }}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { getPageTypesList, createModule } from '@/services/dataService';

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'),
      loading: false,
      isSaving: false,
      moduleName: '',
      newSubmoduleName: '',
      isEditingSubmodule: false,
      editingSubmoduleId: null,
      submodules: [],
      availablePageTypes: [],
      routes: [
        {
          route_name: '',
          page_type_id: '',
          uri: '',
          submodule_name: ''
        }
      ],
      showLeftArrow: false,
      showRightArrow: false
    }
  },
  watch: {
    submodules: {
      handler() {
        this.$nextTick(() => {
          this.checkScroll();
          setTimeout(() => this.checkScroll(), 150);
        });
      },
      deep: true
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await this.fetchPageTypes();
    } finally {
      this.loading = false;
    }
    window.addEventListener('resize', this.checkScroll);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScroll);
  },
  methods: {
    async fetchPageTypes() {
      const toast = useToast();
      try {
        const response = await getPageTypesList(this.token);
        if (response.data.status) {
          this.availablePageTypes = response.data.data.map(type => ({
            id: Number(type.id),
            name: type.type_name
          }));
        } else {
          toast.error(response.data.message || "Failed to retrieve page types");
        }
      } catch (error) {
        toast.error("An error occurred while fetching page types.");
      }
    },
    editSubmodule(subName) {
      this.isEditingSubmodule = true;
      this.editingSubmoduleId = subName;
      this.newSubmoduleName = subName;
    },
    cancelEditSubmodule() {
      this.isEditingSubmodule = false;
      this.editingSubmoduleId = null;
      this.newSubmoduleName = '';
    },
    addSubmodule() {
      const toast = useToast();
      const name = this.newSubmoduleName.trim();
      if (!name) {
        toast.error("Submodule name cannot be empty.");
        return;
      }
      
      const exists = this.submodules.some(n => n.toLowerCase() === name.toLowerCase() && (!this.isEditingSubmodule || n !== this.editingSubmoduleId));
      
      if (exists) {
        toast.error("Submodule already exists.");
        return;
      }

      if (this.isEditingSubmodule) {
        const oldName = this.editingSubmoduleId;
        const index = this.submodules.indexOf(oldName);
        if (index !== -1) {
           this.submodules.splice(index, 1, name);
        }
        // Update references in routes
        this.routes.forEach(r => {
           if (r.submodule_name === oldName) {
              r.submodule_name = name;
           }
        });
        this.cancelEditSubmodule();
      } else {
        this.submodules.push(name);
        this.newSubmoduleName = '';
      }
    },
    removeSubmodule(subName) {
      const toast = useToast();
      const isUsed = this.routes.some(r => r.submodule_name === subName);
      if (isUsed) {
        toast.error(`Cannot delete submodule "${subName}" because it is selected in one or more routes.`);
        return;
      }
      this.submodules = this.submodules.filter(n => n !== subName);
    },
    getRouteCount(subName) {
      return this.routes.filter(r => r.submodule_name === subName).length;
    },
    addRouteRow() {
      this.routes.push({
        route_name: '',
        page_type_id: '',
        uri: '',
        submodule_name: ''
      });
    },
    removeRouteRow(index) {
      this.routes.splice(index, 1);
    },
    cancel() {
      this.$router.push('/module-route-configurations');
    },
    async save() {
      const toast = useToast();
      if (!this.moduleName) {
        toast.error("Module name is required.");
        return;
      }

      this.isSaving = true;
      const upperModuleName = this.moduleName.toUpperCase();
      const module_routes = [];

      for (const route of this.routes) {
        if (!route.route_name || !route.page_type_id || !route.uri) {
           toast.error("Please fill all required fields for all routes.");
           this.isSaving = false;
           return;
        }

        module_routes.push({
           page_type_id: route.page_type_id,
           name: route.route_name,
           uri: route.uri.toLowerCase(),
           submodule_name: route.submodule_name || ""
        });
      }

      const payload = {
        module_name: upperModuleName,
        submodules: this.submodules,
        module_routes: module_routes
      };

      try {
        const response = await createModule(this.token, payload);
        if (response.data.status) {
           toast.success(response.data.message || "Module created successfully");
           this.$router.push('/module-route-configurations');
        } else {
           toast.error(response.data.message || "Failed to create module");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to create module");
      } finally {
        this.isSaving = false;
      }
    },
    scrollLeft() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: -250, behavior: 'smooth' });
      }
    },
    scrollRight() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: 250, behavior: 'smooth' });
      }
    },
    handleScroll() {
      this.checkScroll();
    },
    checkScroll() {
      const container = this.$refs.scrollContainer;
      if (container) {
        this.showLeftArrow = container.scrollLeft > 0;
        this.showRightArrow = Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth;
      }
    }
  }
}
</script>

<style scoped>
.form-group label {
  font-weight: bold;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>